import { closestCalc } from "#src/util/helpers.ts";
import { type Mode, MODES } from "#src/constants/mode.ts";
import { type ProductBase } from "#src/types/products/ProductBase.ts";
import { type ProductType, PRODUCT_TYPE } from "#src/constants/products.ts";
import {
  type QuoteParams,
  type QuoteRequestParams,
  type BenefitPeriodDataItem,
  type InflationDataItem,
  type PartnerDiscountDataItem,
  type PayDurationDataItem,
  type ReturnOfPremiumDataItem,
  type SolveDataItem,
  type RenderRules,
  type ModeDataItem,
  type InflationOption,
  SOLVE,
  PARTNER_DISCOUNT,
  MONTHLY_BENEFITS,
  MONTHLY_BENEFIT_MOST_COMMON,
  PARTNER_DISCOUNT_REQUIREMENTS,
  getInflationDataItem,
  insuredQuoteData,
  INFLATION_OPTION,
  RETURN_OF_PREMIUM_OPTIONS,
  type Solve,
  type ParsingData,
  parseType,
  type ReturnOfPremiumOption,
  type PartnerDiscount,
  parseBoolean,
} from "#src/types/QuoteParams.ts";

export const BENEFIT_PERIOD_OPTIONS: BenefitPeriodDataItem[] = (() => {
  const options = [];
  for (let i = 2; i <= 8; i++) {
    options.push({ value: i, text: `${i} Years` });
  }

  options.push({ value: -1, text: "Unlimited" });
  return options;
})();

export interface LinkedBenefitProductType extends ProductBase {
  isLinkedBenefit: boolean;
  solves: SolveDataItem[];
  partnerDiscountOptions: PartnerDiscountDataItem[];
  returnOfPremiumOptions: ReturnOfPremiumDataItem[];
  payDurationOptions: PayDurationDataItem[];
  inflationOptions: InflationDataItem[];
  benefitPeriodOptions: BenefitPeriodDataItem[];

  eAppParser(
    args: Partial<ParsingData>
  ): LinkedBenefitProductType["defaults"] & {
    face_amount: number;
    mode: Mode;
  };

  defaults: {
    solve: Solve;
    monthly_benefit: number;
    inflation_percentage: InflationOption;
    pay_duration: number;
    benefit_period: number;
    return_of_premium_rider: ReturnOfPremiumOption;
    partner_discount: PartnerDiscount;
    save_age: boolean;
  };
}

function showMonthlyLtcBenefit(params: QuoteParams) {
  return params.solve === SOLVE.BASE_PREMIUM;
}

function showPremium(params: QuoteParams) {
  return params.solve === SOLVE.LTC_BENEFIT;
}

function showHealth(conversion: boolean) {
  return !conversion;
}
export function LinkedBenefit(): LinkedBenefitProductType {
  return {
    get type(): ProductType {
      return PRODUCT_TYPE.LINKED_BENEFIT;
    },
    get isLinkedBenefit(): boolean {
      return true;
    },
    get display(): string {
      return "Linked Benefit";
    },
    get modes(): ModeDataItem[] {
      return MODES;
    },
    get defaults() {
      return {
        solve: SOLVE.BASE_PREMIUM,
        monthly_benefit: MONTHLY_BENEFIT_MOST_COMMON,
        inflation_percentage: null,
        pay_duration: -1,
        benefit_period: 6,
        return_of_premium_rider: null,
        partner_discount: null,
        save_age: false,
      };
    },
    get requestCategory(): string {
      return "universal_life.linked_benefit";
    },
    get solves(): SolveDataItem[] {
      return [
        { value: SOLVE.BASE_PREMIUM, text: "Premium" },
        { value: SOLVE.LTC_BENEFIT, text: "LTC Benefit" },
      ];
    },
    get partnerDiscountOptions(): PartnerDiscountDataItem[] {
      return [
        {
          text: "Single",
          value: PARTNER_DISCOUNT.SINGLE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
        {
          text: "Married, Applying with Spouse",
          value: PARTNER_DISCOUNT.JOINT,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.VERBOSE,
        },
        {
          text: "Married, Applying Individually",
          value: PARTNER_DISCOUNT.SPOUSE,
          requiresJointInfo: PARTNER_DISCOUNT_REQUIREMENTS.NONE,
        },
      ];
    },
    get returnOfPremiumOptions(): ReturnOfPremiumDataItem[] {
      return RETURN_OF_PREMIUM_OPTIONS;
    },
    get payDurationOptions(): PayDurationDataItem[] {
      return [
        { value: 1, title: "Single Pay" },
        { value: 5, title: "5 Pay" },
        { value: 10, title: "10 Pay" },
        { value: -65, title: "Pay to 65" },
        { value: -1, title: "Lifetime Pay" },
      ];
    },
    get inflationOptions(): InflationDataItem[] {
      const items: InflationOption[] = [
        INFLATION_OPTION.NONE,
        INFLATION_OPTION.SIMPLE_3,
        INFLATION_OPTION.COMPOUND_3,
        INFLATION_OPTION.COMPOUND_5,
      ];

      return items.map(getInflationDataItem);
    },
    get benefitPeriodOptions(): BenefitPeriodDataItem[] {
      return BENEFIT_PERIOD_OPTIONS;
    },
    eAppParser(model): ReturnType<LinkedBenefitProductType["eAppParser"]> {
      return {
        face_amount: model.face_amount || 0,
        mode: model.mode as Mode,
        solve: parseType<Solve>(
          model.solve,
          this.solves.map(s => s.value),
          this.defaults.solve
        ),
        monthly_benefit: closestCalc(
          model.monthly_benefit || this.defaults.monthly_benefit,
          MONTHLY_BENEFITS
        ),
        inflation_percentage: parseType<InflationOption>(
          model.inflation_percentage,
          this.inflationOptions.map(v => v.value),
          this.defaults.inflation_percentage
        ),
        pay_duration: parseType<number>(
          model.pay_duration,
          this.payDurationOptions.map(a => a.value),
          this.defaults.pay_duration
        ),
        benefit_period: model.benefit_period
          ? closestCalc(
              model.benefit_period,
              BENEFIT_PERIOD_OPTIONS.map(v => v.value)
            )
          : this.defaults.benefit_period,
        return_of_premium_rider: parseType<ReturnOfPremiumOption>(
          model.return_of_premium_rider,
          this.returnOfPremiumOptions.map(v => v.value),
          this.defaults.return_of_premium_rider
        ),
        partner_discount: parseType<PartnerDiscount>(
          model.partner_discount,
          this.partnerDiscountOptions.map(v => v.value),
          this.defaults.partner_discount
        ),
        save_age: parseBoolean(model.save_age, this.defaults.save_age),
      };
    },
    renderRules(params, options = { conversion: false }): Partial<RenderRules> {
      const shouldShowHealth = showHealth(options.conversion);
      const jointInsuredHealth =
        params.partner_discount === PARTNER_DISCOUNT.JOINT && shouldShowHealth;

      return {
        saveAge: true,
        premium: showPremium(params),
        monthlyBenefit: showMonthlyLtcBenefit(params),
        inflationPercentage: true,
        benefitPeriod: true,
        payDuration: true,
        solve: true,
        partnerDiscount: true,
        returnOfPremium: true,
        insuredHealth: shouldShowHealth,
        jointInsuredHealth: jointInsuredHealth,
        mode: true,
        state: true,
      };
    },
    toQuoteRequest(args): Partial<QuoteRequestParams> {
      const body: Partial<QuoteRequestParams> = {
        agent: { id: args.agentId },
        compare: false,

        ...insuredQuoteData(args.insured),

        selected_type: args.params.selected_type,
        state: args.params.state,
        mode: args.params.mode,
        categories: [this.requestCategory],

        save_age: args.params.save_age,
        solve: args.params.solve,
        inflation_percentage: args.params.inflation_percentage,
        benefit_period: args.params.benefit_period,
        pay_duration: args.params.pay_duration,
        return_of_premium_rider: args.params.return_of_premium_rider,
        partner_discount: args.params.partner_discount,
      };

      if (showMonthlyLtcBenefit(args.params)) {
        body.monthly_benefit = args.params.monthly_benefit;
      } else if (showPremium(args.params)) {
        body.premium = args.params.premium;
      }

      if (
        args.params.partner_discount === PARTNER_DISCOUNT.JOINT &&
        args.otherInsured
      ) {
        body.joint_party = insuredQuoteData(args.otherInsured);
      }

      return body;
    },
  };
}
